import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { searchClients, searchStaff, searchLeads } from '../lib/supabase';
import { useModules, useRole } from './RoleGuard';
import { useAuth } from '../hooks/useAuth';
import { loadRecentClients } from '../lib/recentClients';

// Gated like PAGES below, and like the routes they open: a link nobody can
// follow is not offered.
const QUICK_LINKS = [
  { label: 'Add client', route: '/clients/new', icon: <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/> },
  // Scheduling happens in AxisCare. CAERA's own board is for the lines that
  // moved to it, so the link is offered only to those who can use that board.
  { label: 'Schedule visit', route: '/hh/scheduling', module: 'scheduling', role: 'coordinator', icon: <><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></> },
  { label: 'Add caregiver', route: '/hr/onboarding', module: 'hr', role: 'coordinator', icon: <><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/></> },
  { label: 'Run a report', route: '/reports', icon: <><rect x="2" y="3" width="20" height="14" rx="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/></> },
];

// Pages the search can jump to — the sidebar, flattened. Gated the same way
// the sidebar gates them so nobody is offered a page they can't open.
const PAGES = [
  { label: 'Dashboard', route: '/dashboard', words: 'home' },
  { label: 'Clients', route: '/clients', words: 'patients' },
  { label: 'Leads', route: '/leads', words: 'referrals' },
  { label: 'Intake Queue', route: '/hh/intake', words: 'intake submissions', role: 'coordinator' },
  { label: 'Tasks', route: '/tasks', words: 'todo' },
  { label: 'Prior Auth', route: '/prior-auth', words: 'pa authorization dma' },
  { label: 'General Info', route: '/general-info', words: 'client info' },
  { label: 'Service County Map', route: '/service-county-map', words: 'map counties' },
  { label: 'Pipeline (HH)', route: '/pipeline', words: 'pipeline stages', module: 'hh' },
  { label: 'Calendar', route: '/hh/calendar', words: 'visits schedule evv', module: 'hh' },
  { label: 'Clinical', route: '/hh/clinical', words: 'assessments care plans', module: 'hh' },
  { label: 'Supervisory Visits', route: '/hh/supervisor-visits', words: 'supervision', module: 'hh' },
  { label: 'DME Orders', route: '/dme/orders', words: 'equipment supplies', module: 'dme' },
  { label: 'CMN Tracking', route: '/dme/cmn', words: 'certificate medical necessity', module: 'dme' },
  { label: 'Finance Overview', route: '/finance', words: 'revenue margin', module: 'finance' },
  { label: 'Claims', route: '/finance/claims', words: 'billing claims runs receivables', module: 'finance' },
  { label: 'Payroll', route: '/finance/payroll', words: 'pay', module: 'finance' },
  { label: 'P&L', route: '/finance/pl', words: 'profit loss overhead expenses', module: 'finance' },
  { label: 'Projections', route: '/finance/pl?tab=projections', words: 'forecast scenario what-if', module: 'finance' },
  { label: 'Personnel', route: '/hr/staff-records', words: 'staff caregivers nurses', module: 'hr', role: 'coordinator' },
  { label: 'Onboarding', route: '/hr/onboarding', words: 'hiring applicants', module: 'hr', role: 'manager' },
  { label: 'Training & Compliance', route: '/hr/training-compliance', words: 'certifications courses', module: 'hr', role: 'coordinator' },
  { label: 'Reports & Export', route: '/reports', words: 'export', module: 'quality' },
  { label: 'QA Surveys', route: '/qa', words: 'quality surveys', module: 'quality' },
  { label: 'Incident Log', route: '/hr/disciplinary-log', words: 'incidents disciplinary', module: 'quality', role: 'coordinator' },
  { label: 'Policies & Procedures', route: '/policies', words: 'policy manual', module: 'quality' },
  { label: 'Activity Logs', route: '/activity-logs', words: 'audit history', role: 'coordinator' },
  { label: 'Access Control', route: '/permissions', words: 'permissions roles', role: 'admin' },
  { label: 'User Management', route: '/staff', words: 'users accounts', role: 'admin' },
  { label: 'Agency Portal', route: '/hh/agency-portal', words: 'agency extended living staffing board care plan', module: 'hh', role: 'coordinator' },
  { label: 'Approved Counties', route: '/settings/approved-counties', words: 'service area county approved', role: 'admin' },
  { label: 'Notifications', route: '/notification-preferences', words: 'email alerts' },
];

const lastFirst = (r) => r.last_name ? `${r.last_name}, ${r.first_name}` : (r.full_name || r.first_name || '—');
const SEARCH_ICON = <svg width="14" height="14" fill="none" stroke="var(--text3)" strokeWidth="2" viewBox="0 0 24 24"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>;

export default function Topbar({ title, quickLinks }) {
  const navigate = useNavigate();
  const { hasHR, getAccess } = useModules();
  const { isAdmin, isManager, isCoordinator } = useRole();
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [searching, setSearching] = useState(false);
  // Results carry the query they answer. The panel renders them only while
  // that still matches what is in the box — see the note on `fresh` below.
  const [hits, setHits] = useState({ q: '', clients: [], staff: [], leads: [] });
  // Before anything is typed, the box offers the last clients opened, so
  // getting back to one never needs their name typed again.
  const { profile } = useAuth();
  const [recents, setRecents] = useState([]);
  const typed = query.trim().length >= 2;
  useEffect(() => {
    if (!open || typed) return undefined;
    let live = true;
    loadRecentClients(profile?.id).then((rows) => { if (live) setRecents(rows); });
    return () => { live = false; };
  }, [open, typed, profile?.id]);
  const [active, setActive] = useState(0);
  const inputRef = useRef(null);
  const wrapRef = useRef(null);
  // Every keystroke fires a query and the responses don't arrive in order —
  // the short-prefix ones return more rows and take longer. Without this
  // guard, a stale response for "Arr" could land after the one for "Arroyo"
  // and overwrite it. Only the latest request is allowed to set results.
  const seq = useRef(0);

  const roleOk = (p) => !p.role || (p.role === 'admin' ? isAdmin : p.role === 'manager' ? isManager : isCoordinator);
  const pages = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (q.length < 2) return [];
    return PAGES.filter(p => (!p.module || getAccess(p.module)) && roleOk(p) && (p.label.toLowerCase().includes(q) || p.words.includes(q))).slice(0, 4);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, isAdmin, isManager, isCoordinator]);

  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) { setHits({ q: '', clients: [], staff: [], leads: [] }); setSearching(false); return undefined; }
    const mine = ++seq.current;
    setSearching(true);
    const t = setTimeout(async () => {
      const [c, l, s] = await Promise.all([searchClients(q), searchLeads(q), hasHR ? searchStaff(q) : Promise.resolve({ data: [] })]);
      if (mine !== seq.current) return;                       // a newer keystroke superseded this one
      setHits({ q, clients: c.data || [], leads: l.data || [], staff: s.data || [] });
      setSearching(false);
      setActive(0);
    }, 180);
    return () => clearTimeout(t);
  }, [query, hasHR]);

  // Results from the database lag the box by a debounce plus a round trip, so
  // between keystrokes `hits` still answers an earlier prefix. Showing those
  // is worse than showing nothing: typing "brown" would list the first eight
  // clients matching "br" — six people with no "brown" about them — and they
  // read as real matches, so it is easy to click the wrong client. Pages are
  // filtered synchronously and are always current, so they keep rendering.
  const fresh = hits.q === query.trim();

  // One flat, ordered list so the arrow keys can walk every section.
  const flat = useMemo(() => (!typed ? recents.map(r => ({ kind: 'Recent clients', key: `r${r.id}`, title: lastFirst(r), ref: r.client_id, meta: r.current_status, route: `/clients/${r.id}` })) : fresh ? [
    ...hits.clients.map(r => ({ kind: 'Clients', key: `c${r.id}`, title: lastFirst(r), ref: r.client_id, meta: r.pa_match ? `PA ${r.pa_match}` : r.current_status, route: `/clients/${r.id}` })),
    ...hits.leads.map(r => ({ kind: 'Leads', key: `l${r.id}`, title: lastFirst(r), ref: r.lead_id, meta: r.converted_client_id ? 'Converted' : r.current_status, route: `/leads/${r.id}` })),
    ...hits.staff.map(r => ({ kind: 'Staff', key: `s${r.id}`, title: lastFirst(r), ref: r.employee_id || r.position_code || '—', meta: r.status, route: `/hr/personnel/${r.id}` })),
  ] : []).concat(
    pages.map(p => ({ kind: 'Pages', key: `p${p.route}`, title: p.label, ref: '', meta: p.route, route: p.route })),
  ), [hits, pages, fresh, typed, recents]);

  const go = (item) => { if (!item) return; navigate(item.route); setQuery(''); setOpen(false); inputRef.current?.blur(); };

  // "/" or ⌘K / Ctrl+K focuses the box from anywhere, the way AxisCare's
  // "Jump to anywhere" does. Ignored while typing in another field.
  useEffect(() => {
    const onKey = (e) => {
      const tag = (e.target.tagName || '').toLowerCase();
      const typing = tag === 'input' || tag === 'textarea' || tag === 'select' || e.target.isContentEditable;
      if ((e.key === 'k' && (e.metaKey || e.ctrlKey)) || (e.key === '/' && !typing)) { e.preventDefault(); inputRef.current?.focus(); setOpen(true); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
  useEffect(() => {
    const onDown = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, []);

  const onKeyDown = (e) => {
    if (e.key === 'Escape') { setOpen(false); inputRef.current?.blur(); return; }
    if (!flat.length) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive(a => (a + 1) % flat.length); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive(a => (a - 1 + flat.length) % flat.length); }
    else if (e.key === 'Enter') { e.preventDefault(); go(flat[active]); }
  };

  const showPanel = open && (typed || recents.length > 0);
  const placeholder = hasHR ? 'Search clients, leads, staff, IDs, PA numbers, pages…' : 'Search clients, leads, IDs, PA numbers, pages…';
  let cursor = -1;

  return (
    <div style={{ background: 'var(--topbar-bg)', borderBottom: '0.5px solid var(--border)', padding: quickLinks ? '10px 24px' : '0 24px', display: 'flex', flexDirection: 'column', gap: 10, flexShrink: 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, height: quickLinks ? undefined : 56 }}>
        <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--text)', flex: 1 }}>{title}</div>

        <div ref={wrapRef} style={{ position: 'relative' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 8, padding: '7px 12px', width: 300 }}>
            {SEARCH_ICON}
            <input
              ref={inputRef}
              value={query}
              onChange={e => { setQuery(e.target.value); setOpen(true); }}
              onFocus={() => setOpen(true)}
              onKeyDown={onKeyDown}
              placeholder={placeholder}
              aria-label="Search"
              style={{ border: 'none', background: 'transparent', fontFamily: 'inherit', fontSize: 13, color: 'var(--text)', outline: 'none', width: '100%' }}
            />
            {searching
              ? <div className="loading-spinner" style={{ width: 14, height: 14 }} />
              : <kbd style={{ fontFamily: 'var(--mono)', fontSize: 9.5, color: 'var(--text3)', border: '0.5px solid var(--border2)', borderRadius: 4, padding: '1px 5px', lineHeight: 1.4, flexShrink: 0 }}>/</kbd>}
          </div>

          {showPanel && (
            <div style={{ position: 'absolute', top: '110%', right: 0, width: 460, maxWidth: 'calc(100vw - 32px)', background: 'var(--topbar-bg)', border: '1px solid var(--border)', borderRadius: 8, boxShadow: 'var(--shadow)', zIndex: 100, overflow: 'hidden', maxHeight: 420, overflowY: 'auto' }}>
              {flat.length === 0 && searching && (
                <div style={{ padding: '14px', fontSize: 12.5, color: 'var(--text3)' }}>Searching for “{query.trim()}”…</div>
              )}
              {flat.length === 0 && !searching && (
                <div style={{ padding: '14px', fontSize: 12.5, color: 'var(--text3)' }}>Nothing matches “{query.trim()}”</div>
              )}
              {['Recent clients', 'Clients', 'Leads', 'Staff', 'Pages'].map(kind => {
                const items = flat.filter(i => i.kind === kind);
                if (items.length === 0) return null;
                return (
                  <div key={kind}>
                    <div style={{ padding: '6px 14px', fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text3)', background: 'var(--bg2)' }}>{kind}</div>
                    {items.map(item => {
                      cursor += 1; const i = cursor;
                      return (
                        <div key={item.key} onMouseDown={e => e.preventDefault()} onClick={() => go(item)} onMouseEnter={() => setActive(i)}
                          style={{ padding: '9px 14px', cursor: 'pointer', borderBottom: '0.5px solid var(--border)', display: 'flex', alignItems: 'center', gap: 10, background: active === i ? 'var(--bg)' : 'transparent' }}>
                          {item.ref && <span style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--text3)', flexShrink: 0, minWidth: 46 }}>{item.ref}</span>}
                          <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--text)', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.title}</span>
                          <span style={{ fontSize: 11, color: 'var(--text3)', flexShrink: 1, maxWidth: '38%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={item.meta}>{item.meta}</span>
                        </div>
                      );
                    })}
                  </div>
                );
              })}
              {flat.length > 0 && (
                <div style={{ padding: '6px 14px', fontSize: 10.5, color: 'var(--text3)', display: 'flex', gap: 12 }}>
                  <span>↑↓ move</span><span>↵ open</span><span>esc close</span>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {quickLinks && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 9, flexWrap: 'wrap' }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--accent2)', flexShrink: 0, paddingRight: 3 }}>
            <svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/></svg>Quick links
          </span>
          <span style={{ width: '0.5px', height: 18, background: 'var(--border)', flexShrink: 0 }} />
          {QUICK_LINKS.filter(q => (!q.module || getAccess(q.module)) && roleOk(q)).map(q => (
            <button key={q.label} onClick={() => navigate(q.route)} className="btn btn-sm btn-outline" style={{ display: 'flex', alignItems: 'center', gap: 7, whiteSpace: 'nowrap' }}>
              <span style={{ display: 'flex', color: 'var(--copper, var(--accent))' }}><svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="1.9" viewBox="0 0 24 24">{q.icon}</svg></span>
              {q.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
